/**
 * Карточка заказа.
 *
 * До неё заказ был виден только строкой в таблице: открыть, посмотреть состав,
 * отменить или перенести было нельзя вообще — ручки в API есть, экрана не было.
 *
 * Здесь же оператор закрывает доставку руками. Это не дубль приложения
 * водителя, а замена ему в двух живых случаях: у водителя сел телефон, либо
 * воду отдали на складе самовывозом. Поэтому форма требует те же цифры —
 * сколько полных отдали, сколько пустых забрали, сколько денег взяли:
 * доставка без этих трёх чисел не проводится ни с телефона, ни отсюда.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, dateFmt, dateTimeFmt, money, PAYMENT_RU, phoneFmt } from '../api';
import { useApi } from '../useApi';
import { Counter, Empty, ErrorBox, Loading, Status, Tile, useToast } from '../ui';

type Order = {
  id: string;
  number: string;
  status: string;
  deliveryDate: string;
  timeFrom: string | null;
  timeTo: string | null;
  totalKop: string;
  plannedGive: number;
  plannedTake: number;
  paymentMethod: string;
  comment: string | null;
  cancelReason: string | null;
  createdAt: string;
  client: { id: string; code: string; name: string; phone: string; cachedBalanceKop: string; cachedBottles: number };
  address: { street: string; house: string; flat: string | null; comment: string | null };
  items: Array<{ id: string; name: string; qty: number; priceKop: string; bottleDelta: number }>;
  delivery: {
    id: string;
    status: string;
    givenFull: number;
    takenEmpty: number;
    paidKop: string;
    comment: string | null;
    createdAt: string;
    driver: { name: string } | null;
  } | null;
  moneyEntries: Array<{ id: string; kind: string; amountKop: string }>;
  bottleEntries: Array<{ id: string; kind: string; qty: number }>;
  routeStop: { id: string; position: number; route: { id: string; date: string; status: string } } | null;
};

export function OrderCard() {
  const { id } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const { data: o, error, loading, reload } = useApi<Order>(id ? `/orders/${id}` : null);
  const [busy, setBusy] = useState(false);
  const [act, setAct] = useState<'none' | 'move' | 'deliver'>('none');
  const [fail, setFail] = useState<unknown>(null);

  if (loading) return <Loading what="Открываю заказ" />;
  if (error) return <ErrorBox error={error} />;
  if (!o) return null;

  /** Заказ, по которому уже прошла доставка, не отменяется и не переносится. */
  const settled = !!o.delivery || o.status === 'CANCELED';

  const run = async (what: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setFail(null);
    try {
      await what();
      await reload();
      setAct('none');
      toast.show(ok);
    } catch (e) {
      setFail(e);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    const reason = prompt('Причина отмены (останется в истории)');
    if (!reason) return;
    await run(() => api(`/orders/${o.id}/cancel`, { body: { reason } }), 'Заказ отменён');
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>
            Заказ <span className="mono">{o.number}</span> <Status value={o.status} />
          </h1>
          <div className="muted">
            создан {dateTimeFmt(o.createdAt)} · доставка {dateFmt(o.deliveryDate)}
            {o.timeFrom ? ` с ${o.timeFrom} до ${o.timeTo ?? ''}` : ''}
          </div>
        </div>
        <button className="ghost" onClick={() => nav(-1)}>
          ← Назад
        </button>
      </div>

      <div className="grid c4" style={{ marginBottom: 16 }}>
        <Tile label="Сумма" value={money(o.totalKop)} sub={PAYMENT_RU[o.paymentMethod] ?? o.paymentMethod} />
        <Tile label="Везём / забираем" value={`+${o.plannedGive} / −${o.plannedTake}`} sub="бутылей по плану" />
        <Tile
          label="Клиент"
          value={<Link to={`/clients/${o.client.id}`}>{o.client.name}</Link>}
          sub={phoneFmt(o.client.phone)}
        />
        <Tile
          label="В маршруте"
          value={o.routeStop ? `точка ${o.routeStop.position}` : '—'}
          sub={o.routeStop ? <Link to={`/board?date=${o.deliveryDate.slice(0, 10)}`}>открыть доску</Link> : 'не распределён'}
        />
      </div>

      {o.cancelReason ? (
        <div className="card" style={{ borderColor: 'var(--red)' }}>
          <b>Заказ отменён.</b> <span className="muted">{o.cancelReason}</span>
        </div>
      ) : null}

      <div className="row">
        <div className="card" style={{ flex: '2 1 420px' }}>
          <h2>Состав</h2>
          <table>
            <thead>
              <tr>
                <th>Товар</th>
                <th className="num">Кол-во</th>
                <th className="num">Цена</th>
                <th className="num">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {o.items.map((i) => (
                <tr key={i.id}>
                  <td>{i.name}</td>
                  <td className="num">{i.qty}</td>
                  <td className="num">{money(i.priceKop)}</td>
                  <td className="num">{money(BigInt(i.priceKop) * BigInt(i.qty))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {o.comment ? <div className="muted" style={{ marginTop: 10 }}>{o.comment}</div> : null}
        </div>

        <div className="card" style={{ flex: '1 1 280px' }}>
          <h2>Адрес</h2>
          <div>
            {o.address.street}, {o.address.house}
            {o.address.flat ? `, кв. ${o.address.flat}` : ''}
          </div>
          {o.address.comment ? <div className="muted">{o.address.comment}</div> : null}
          <div className="muted" style={{ marginTop: 12, fontSize: 12 }}>
            Баланс клиента: {money(o.client.cachedBalanceKop)} · тара на руках {o.client.cachedBottles} шт.
          </div>
        </div>
      </div>

      {/* ── Факт доставки ──────────────────────────────────────────────── */}
      {o.delivery ? (
        <div className="card">
          <h2>Доставлено</h2>
          <div className="row">
            <Tile label="Отдано полных" value={o.delivery.givenFull} />
            <Tile label="Забрано пустых" value={o.delivery.takenEmpty} />
            <Tile label="Оплачено" value={money(o.delivery.paidKop)} />
            <Tile label="Кто" value={o.delivery.driver?.name ?? '—'} sub={dateTimeFmt(o.delivery.createdAt)} />
          </div>
          {o.delivery.comment ? <div className="muted">{o.delivery.comment}</div> : null}
          <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
            Проводок по заказу: денег {o.moneyEntries.length}, тары {o.bottleEntries.length}. Ошибка
            в доставке правится сторно в карточке клиента, а не правкой этого экрана.
          </div>
        </div>
      ) : null}

      {/* ── Действия ───────────────────────────────────────────────────── */}
      <div className="card">
        <h2>Что можно сделать</h2>
        <ErrorBox error={fail} />
        {settled ? (
          <Empty>
            {o.delivery
              ? 'Заказ отработан. Отмена и перенос закрыты: за ним деньги и тара.'
              : 'Заказ отменён. Новый создаётся в карточке клиента.'}
          </Empty>
        ) : (
          <>
            <div className="row" style={{ gap: 8 }}>
              <button className="primary" disabled={busy} onClick={() => setAct(act === 'deliver' ? 'none' : 'deliver')}>
                Провести доставку
              </button>
              <button disabled={busy} onClick={() => setAct(act === 'move' ? 'none' : 'move')}>
                Перенести на другую дату
              </button>
              <button className="ghost" disabled={busy} onClick={() => void cancel()}>
                Отменить заказ
              </button>
            </div>

            {act === 'move' ? <MoveForm order={o} busy={busy} onSubmit={(date) =>
              run(() => api(`/orders/${o.id}/reschedule`, { body: { date } }), `Перенесён на ${dateFmt(date)}`)
            } /> : null}

            {act === 'deliver' ? <DeliverForm order={o} busy={busy} onSubmit={(body) =>
              run(() => api(`/orders/${o.id}/complete`, { body }), 'Доставка проведена')
            } /> : null}
          </>
        )}
      </div>
      {toast.node}
    </>
  );
}

function MoveForm({ order, busy, onSubmit }: { order: Order; busy: boolean; onSubmit: (date: string) => void }) {
  const [date, setDate] = useState(order.deliveryDate.slice(0, 10));
  return (
    <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
      <div className="field" style={{ flex: '0 0 180px', marginBottom: 0 }}>
        <label>Новая дата доставки</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <button className="primary" disabled={busy} onClick={() => onSubmit(date)}>
        Перенести
      </button>
      <div className="muted" style={{ fontSize: 12, flex: 1 }}>
        Заказ уедет на новую дату вместе с составом и окном доставки. На старой дате он закроется
        как перенесённый — иначе воду привезут дважды.
      </div>
    </div>
  );
}

/** Закрытие доставки руками. Те же три числа, что спрашивают у водителя. */
function DeliverForm({
  order,
  busy,
  onSubmit,
}: {
  order: Order;
  busy: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [status, setStatus] = useState<'DONE' | 'PARTIAL' | 'FAILED'>('DONE');
  const [given, setGiven] = useState(order.plannedGive);
  const [taken, setTaken] = useState(order.plannedTake);
  const [rub, setRub] = useState((Number(order.totalKop) / 100).toFixed(2));
  const [method, setMethod] = useState(order.paymentMethod === 'CREDIT' ? 'CREDIT' : 'CASH');
  const [reason, setReason] = useState('');
  const failed = status === 'FAILED';

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--n200)' }}>
      <div className="tabs" style={{ marginBottom: 12 }}>
        {(['DONE', 'PARTIAL', 'FAILED'] as const).map((s) => (
          <button key={s} className={status === s ? 'active' : ''} onClick={() => setStatus(s)}>
            {s === 'DONE' ? 'Доставлено' : s === 'PARTIAL' ? 'Частично' : 'Не доставлено'}
          </button>
        ))}
      </div>

      {failed ? (
        <div className="field" style={{ maxWidth: 420 }}>
          <label>Почему не доставлено</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Дверь не открыли" />
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Ни деньги, ни тара не двинутся. Заказ встанет «не доставлен», новую дату назначает оператор.
          </div>
        </div>
      ) : (
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <Counter label="Отдано полных" value={given} onChange={setGiven} max={99} />
          <Counter label="Забрано пустых" value={taken} onChange={setTaken} max={99} />
          <div className="field" style={{ flex: '0 0 150px' }}>
            <label>Взято денег, ₽</label>
            <input inputMode="decimal" value={rub} onChange={(e) => setRub(e.target.value)} />
          </div>
          <div className="field" style={{ flex: '0 0 180px' }}>
            <label>Чем платил</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              {Object.entries(PAYMENT_RU).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <button
          className="primary"
          disabled={busy || (failed && reason.length < 3)}
          onClick={() =>
            onSubmit({
              status,
              givenFull: failed ? 0 : given,
              takenEmpty: failed ? 0 : taken,
              paidKop: failed ? '0' : String(Math.round(Number(rub.replace(',', '.')) * 100) || 0),
              paymentMethod: failed ? undefined : method,
              failReason: failed ? reason : undefined,
              // Повтор запроса не создаёт вторую проводку: ключ привязан к заказу
              // и к попытке, а не к моменту нажатия.
              idempotencyKey: `op-deliver-${order.id}-${status}`,
            })
          }
        >
          Провести
        </button>
        <div className="muted" style={{ fontSize: 12, flex: 1, alignSelf: 'center' }}>
          Деньги и тара спишутся одной транзакцией. Повторное нажатие второй проводки не создаст.
        </div>
      </div>
    </div>
  );
}
